import type { OptionDB } from "@project/emerge";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { HingeType } from "../interfaces/HingeType";
import type { LockType } from "../interfaces/LockType";

function hasLegacyMeshTopBottom(ps: Record<string, unknown>): boolean {
  return Boolean(ps.meshTop || ps.meshBottom);
}

function cloneOption<T>(value: T): T {
  return structuredClone(value) as T;
}

function hydrateLockPartSelection(
  ps: Record<string, unknown>,
  optionDb: OptionDB,
): LockType | undefined {
  if (!hasLegacyMeshTopBottom(ps)) return undefined;
  const name = typeof ps.name === "string" ? ps.name : "";
  if (!name) return undefined;
  return cloneOption(
    optionDb.getByName<LockType>(LTCaseUserOption.LockType, name),
  );
}

function hydrateHingePartSelection(
  ps: Record<string, unknown>,
  optionDb: OptionDB,
): HingeType | undefined {
  if (!hasLegacyMeshTopBottom(ps)) return undefined;
  const name = typeof ps.name === "string" ? ps.name : "";
  if (!name) return undefined;
  return cloneOption(
    optionDb.getByName<HingeType>(LTCaseUserOption.HingeType, name),
  );
}

/**
 * Imported connector sets often still have legacy `meshTop` / `meshBottom` on
 * `partSelection` while `migratePartSelection` skipped merging because `meshes`
 * was already partially present. Fresh cases use the catalog `LockType` /
 * `HingeType` from `data.json` (`meshes.top` / `meshes.bottom`). Replacing here
 * lets `ProfileBuildStep` resolve `meshes[top|bottom]` and emit `meshes.default`
 * like newly created locks/hinges.
 */
export function hydrateImportedConnectorPartSelections(
  settings: FlightCaseSettings,
  optionDb: OptionDB,
): void {
  for (const opt of settings.variableOptions ?? []) {
    if (!opt || typeof opt !== "object") continue;
    const vo = opt as unknown as Record<string, unknown>;
    const vt = vo.variableOptionType;

    if (vt === VariableOptionType.LOCK_SET) {
      const ps = vo.partSelection;
      if (!ps || typeof ps !== "object") continue;
      const hydrated = hydrateLockPartSelection(
        ps as Record<string, unknown>,
        optionDb,
      );
      if (hydrated) vo.partSelection = hydrated;
      continue;
    }

    if (vt === VariableOptionType.HINGE_SET) {
      const ps = vo.partSelection;
      if (!ps || typeof ps !== "object") continue;
      const hydrated = hydrateHingePartSelection(
        ps as Record<string, unknown>,
        optionDb,
      );
      if (hydrated) vo.partSelection = hydrated;
    }
  }
}
